import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { VaultItem } from '@heybesafe/shared';
import { sortItemsForSafeList, isBinderType, SAFE_LIST_SORT_ORDERS } from '@heybesafe/shared';
import type { SafeListSortOrder } from '@heybesafe/shared';
import { getSafeListSort, setSafeListSort } from '../storage/safe-store';
import { useSafe } from '../context/SafeContext';
import { HbsButton } from '../components/hbs/Button';
import { Icon } from '../components/hbs/Icon';
import { SafeItemRow } from '../components/SafeItemRow';
import { CollapsibleFamilySection } from '../components/CollapsibleFamilySection';
import { OnboardingChecklist } from '../onboarding/OnboardingChecklist';
import { BiometricUnlockCta } from '../components/BiometricUnlockCta';

/** Arrow convention: ↓ descending (newest first, Z–A), ↑ ascending. The words
 *  carry the meaning; the arrow is the at-a-glance cue. */
const SORT_LABELS: Record<SafeListSortOrder, string> = {
  'updated-desc': '↓ Last changed (newest first)',
  'updated-asc':  '↑ Last changed (oldest first)',
  'name-asc':     '↑ Name (A–Z)',
  'name-desc':    '↓ Name (Z–A)',
};

export default function SafePage(): JSX.Element {
  const { items, itemsLoaded, orgs = [], loadingStep } = useSafe();
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const searchActive = query.trim().length > 0;

  function setQuery(value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }

  // Binder types (contacts, documents, schedules) live in the Binder / My
  // Contacts, never the Safe — exclude them here (personal + family sections).
  // Payment cards live on their own My Payment Cards surface, so exclude `card`
  // too (both personal and any future shared card).
  const safeItems = useMemo(
    () => items.filter((i) => !isBinderType(i.type) && i.type !== 'card'),
    [items],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? safeItems.filter(
          (i) =>
            i.name.toLowerCase().includes(q) ||
            (i.username ?? '').toLowerCase().includes(q) ||
            (i.url ?? '').toLowerCase().includes(q)
        )
      : safeItems;
  }, [safeItems, query]);

  // Seeded from the stored preference so the list opens in the order the user
  // last chose, rather than snapping to the default and re-sorting.
  const [sortOrder, setSortOrder] = useState<SafeListSortOrder>(() => getSafeListSort());
  function chooseSort(order: SafeListSortOrder): void {
    setSortOrder(order);
    setSafeListSort(order);
  }

  const personalItems = useMemo(
    () => sortItemsForSafeList(filtered.filter((i) => !i.orgId), sortOrder),
    [filtered, sortOrder],
  );

  // Family items grouped by org, name resolved from `orgs` (fallback "Family
  // Safe"), sorted alphabetically. Only families with matching items appear.
  // Within each family group, sensitive items sort to the bottom (same two-pass
  // as the personal list).
  // Families that still have active coverage. Uncovered families are hidden
  // entirely on the Safe page (fail-open: absent coverage → covered).
  const coveredOrgIds = useMemo(
    () => new Set(orgs.filter((o) => o.covered !== false).map((o) => o.id)),
    [orgs],
  );

  const familyGroups = useMemo(() => {
    const byOrg = new Map<string, VaultItem[]>();
    for (const item of filtered) {
      if (!item.orgId || !coveredOrgIds.has(item.orgId)) continue;
      const list = byOrg.get(item.orgId) ?? [];
      list.push(item);
      byOrg.set(item.orgId, list);
    }
    return [...byOrg.entries()]
      .map(([orgId, groupItems]) => ({
        orgId,
        familyName: orgs.find((o) => o.id === orgId)?.name ?? 'Family Safe',
        items: sortItemsForSafeList(groupItems, sortOrder),
      }))
      // Family sections keep their own alphabetical order by family name and
      // always render above the personal list — the item sort applies INSIDE a
      // family, never to the families themselves.
      .sort((a, b) =>
        a.familyName.localeCompare(b.familyName, undefined, { sensitivity: 'base' })
      );
  }, [filtered, orgs, coveredOrgIds, sortOrder]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="sticky top-0 bg-hbs-bg z-10 border-b border-hbs-border px-5 pt-[18px] pb-3">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <h1 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text">
              My Safe
            </h1>
            <p className="text-[12px] text-hbs-text-mute mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span>{filtered.length} {filtered.length === 1 ? 'item' : 'items'} ·</span>
              {/* The old label claimed "sorted by name" while the list was
                  ordered by last-changed. It now reflects the actual order. */}
              <label className="sr-only" htmlFor="safe-sort-select">Sort items by</label>
              <select
                id="safe-sort-select"
                value={sortOrder}
                onChange={(e) => chooseSort(e.target.value as SafeListSortOrder)}
                className="bg-transparent text-[12px] text-hbs-text-mute hover:text-hbs-text focus:text-hbs-text border-0 p-0 pr-1 cursor-pointer focus:outline-none focus:underline"
              >
                {SAFE_LIST_SORT_ORDERS.map((o) => (
                  <option key={o} value={o}>{SORT_LABELS[o]}</option>
                ))}
              </select>
            </p>
          </div>
          <HbsButton
            id="new-safe-item-button"
            title="Add a new item to your safe"
            onClick={() => navigate('/safe/new')}
            leadingIcon={<Icon name="plus" size={14} />}
          >
            New
          </HbsButton>
        </div>
        <div className="mt-2 flex items-center gap-2 h-9 px-2.5 bg-hbs-surface border border-hbs-border rounded-hbs-sm">
          <Icon name="search" size={15} className="text-hbs-text-mute" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this list"
            className="flex-1 min-w-0 bg-transparent text-[13.5px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none"
          />
          {/* Clear search in one click. Only shown while there's a query so the
              bar stays uncluttered when empty. */}
          {query && (
            <button
              id="safe-search-clear"
              type="button"
              aria-label="Clear search"
              title="Clear search"
              onClick={() => setQuery('')}
              className="shrink-0 p-0.5 rounded-hbs-sm text-hbs-danger hover:bg-hbs-surface-alt focus:outline-none focus-visible:ring-1 focus-visible:ring-hbs-danger"
            >
              <Icon name="x" size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Item list */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* First-run onboarding: welcome modal + getting-started checklist.
            Rendered above the list so it shows even on an empty safe. Self-hides
            once onboarding is complete or dismissed (and never for existing users). */}
        <OnboardingChecklist />
        {/* Nudge to turn on biometric unlock — only when supported, not yet
            enrolled, and not dismissed. Self-hides otherwise. */}
        <BiometricUnlockCta />
        {/* loadingStep alone was not enough: unlock() flips status to 'unlocked'
            and only sets loadingStep several round-trips later, so this pane
            was skipped during exactly the window it exists to cover. Key it on
            itemsLoaded and keep loadingStep for its more specific wording. */}
        {!itemsLoaded && items.length === 0 ? (
          <div role="status" aria-live="polite" className="flex items-center justify-center h-full text-hbs-text-mute text-[13px]">
            {loadingStep ?? 'Loading your safe…'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-hbs-text-mute gap-2 p-8 text-center">
            <Icon name="safe" size={40} className="text-hbs-text-mute" />
            <p className="text-[13px]">{query ? 'No matches' : 'Your safe is empty'}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/* Family sections rendered FIRST so users don't have to scroll
                past a long personal list to find them. Uncovered families are
                hidden entirely (no locked card) — see coveredOrgIds. */}
            {familyGroups.map((group) => (
              <CollapsibleFamilySection
                key={group.orgId}
                id={`family-section-toggle-${group.orgId}`}
                familyName={group.familyName}
                forceExpanded={searchActive}
              >
                {group.items.map((item) => (
                  <li key={item.id}>
                    <SafeItemRow item={item} selected={selectedId === item.id} />
                  </li>
                ))}
              </CollapsibleFamilySection>
            ))}
            {personalItems.length > 0 && (
              <ul className="flex flex-col gap-px">
                {personalItems.map((item) => (
                  <li key={item.id}>
                    <SafeItemRow item={item} selected={selectedId === item.id} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
